import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Location, PlatformLocation, ViewportScroller } from '@angular/common';
import {
  AppBaseComponent,
  DataCollectionService,
  doArrayDirtyCheck,
  getDeepestRouteSnapshot,
  handleUnsavedArrays,
  Instance,
  InstanceResolveData,
  LoadingService,
  OverlayService,
  ViewType,
} from '@finra/rampdi-forms-common';
import { InstanceService, MU3WithSupportingData } from './services/instance.service';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationExtras,
  NavigationStart,
  Router,
  RoutesRecognized,
} from '@angular/router';
import { BeastClientService, BeastEvent } from '@finra/beast-client';
import { CompletenessCheckService } from './services/completeness-check.service';
import { NavigationDataService } from './services/navigation-data.service';
import { RedlineService } from './services/redline.service';
import { environment } from '../environments/environment';
import { distinctUntilChanged, filter, map, pairwise, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { DisplayRulesService, FilingStatusDisplayRules, FILING_STATUS } from './services/display-rules.service';
import { BehaviorSubject, firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { HostApiService } from 'lib-host-interface';
import { toSignal } from '@angular/core/rxjs-interop';
import { SharedStateService } from './services/shared-state.service';
import { ExternalService } from '@finra/angular-data-collection-sdk';
import { GlobalContext } from './services/global-context.service';

export const BEAST_EVENTS: {
  [key: string]: { description: string; resource: string; action: string; recents?: any };
} = {
  'fv:redlining': {
    description: 'Redline toggle from footer',
    action: 'Click',
    resource: 'FV:Redlining',
  },
  print: {
    description: 'Click on Print View from the footer',
    resource: 'FV:PrintForm',
    action: 'Click',
  },
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: {
    '(window:afterprint)': 'onAfterPrint()',
    '(window:beforeprint)': 'onBeforePrint()',
    '(window:resize)': 'getScreenSize($event)',
  },
})
export class AppComponent
  extends AppBaseComponent<
    MU3WithSupportingData,
    Instance<any>,
    InstanceResolveData<MU3WithSupportingData, Instance<any>>
  >
  implements OnInit, OnDestroy, AfterViewInit
{
  lastOpenedSectionDE = 'identifyingInformation';
  hostApi: HostApiService = inject(HostApiService);
  private externalRoutingEventsSub: Subscription;
  hasRoutingError = false;
  concurrencyModal: boolean = false;
  migrationModal: boolean = false;
  @ViewChild('disclosureDeleteModal', { static: true }) disclosureDeleteModal: TemplateRef<any>;
  navrouteData: any;
  isStandalone = !this.platformLocation.hostname.includes('gateway');
  displayRulesService = inject(DisplayRulesService);
  displayRules$: Observable<FilingStatusDisplayRules> = inject(DisplayRulesService).displayRulesSubj;
  private isDestroying$ = new Subject<void>();
  private phoneAutocompleteObserver: MutationObserver;
  hasReadOnlyMode$$ = toSignal(
    this.displayRules$.pipe(
      takeUntil(this.isDestroying$),
      map((res) => res && Object.keys(res)?.length > 0 && res['readOnlyMode']),
    ),
  );
  isMobileView: boolean = false;
  private window = window;
  loggedInUserName: any;
  subjectId: string = '';
  hasConcurrencyIssue: boolean = false;
  displayMigrationBanner: boolean = false;
  migrationInProgress: boolean = false;
  migrationInstanceId: string = '';
  isPostSubmission: boolean;
  isGuidance: boolean;
  formView: boolean;
  inProgressNav: boolean;
  isRegulatorUser: boolean = false;
  saveInProgress: boolean = false;

  protected get environment(): typeof environment {
    return environment;
  }

  constructor(
    public route: ActivatedRoute,
    private router: Router,
    private navDataService: NavigationDataService,
    private instanceService: InstanceService,
    private loadingService: LoadingService,
    private OS: OverlayService,
    private location: Location,
    private cd: ChangeDetectorRef,
    private beast: BeastClientService,
    private DCS: DataCollectionService,
    private CC: CompletenessCheckService,
    private RL: RedlineService,
    private platformLocation: PlatformLocation,
    private VPS: ViewportScroller,
    public elementRef: ElementRef,
    private sharedStateService: SharedStateService,
    private ES: ExternalService,
    private DS: DisplayRulesService,
    private globalContext: GlobalContext,
    private ngZone: NgZone,
  ) {
    router.initialNavigation();
    super(route, router, cd, loadingService, OS, CC, RL, beast, location, instanceService, true);
    this.beastEvent = BEAST_EVENTS;
    (this.elementRef.nativeElement as any).shouldWarnBeforeExit = this.shouldWarnBeforeExit.bind(this);
    DS.isRegulatorUser.pipe(takeUntil(this.isDestroying$)).subscribe((res) => {
      this.isRegulatorUser = res;
    });
  }

  get userName(): string {
    const info = this.globalContext.store().userInfo;
    return info?.firstName ? `${info.firstName} ${info.lastName}` : '';
  }

  closeProcessedModal(event?) {
    if (event?.detail?.continue) {
      this.isPostSubmission = true;
      this.sharedStateService.setPostSubmission(true);
      this.router.navigate(['post-submission'], { queryParamsHandling: 'merge' });
    }
  }

  onSubmissionConfirmationOk() {
    this.closeProcessedModal({ detail: { continue: true } });
  }

  getInitialViewBeastEvent(navEvent?: any): BeastEvent {
    const instance = this.getDeepestRouteInst();
    const filingTypeCode = instance?.instanceMetadata?.attributes?.filingTypeCode;
    const id = instance?.id;
    const eventConfig = this.beastEvent[`view:${filingTypeCode}`];

    return {
      action: eventConfig?.action ?? 'View',
      resource: eventConfig?.resource ?? `MU3:${filingTypeCode}`,
      correlationId: id,
      details: {
        ...eventConfig?.recents,
        description: eventConfig?.description,
        url: navEvent?.url,
        urlAfterRedirects: navEvent?.urlAfterRedirects,
        recentsLabel: `${instance?.templateName?.toUpperCase()} id`,
      },
    };
  }

  ngOnInit(): void {
    this.getScreenSize();
    this.setupPhoneAutocompleteHandler();
    this.navData$ = this.router.events.pipe(
      tap((navEvent) => this.tapNavEvent(navEvent)),
      filter((event) => event instanceof RoutesRecognized || event instanceof NavigationEnd),
      distinctUntilChanged(),
      pairwise(),
      filter(([e1, e2]) => e1 instanceof RoutesRecognized && e2 instanceof NavigationEnd),
      withLatestFrom(this.CC.completenessCheck$),
      map(([[e1, e2], cc]) => this.mapNavEvent(cc)),
    );

    this.hostApi.routing?.events?.pipe(takeUntil(this.isDestroying$)).subscribe((event) => {
      console.log(`app-mu3-form received app host routing event`, event);
    });

    this.CC.openConcurrencyModal$?.pipe(takeUntil(this.isDestroying$)).subscribe((openConcurrencyModal: boolean) => {
      this.concurrencyModal = openConcurrencyModal;
    });

    this.DS.navigationError.pipe(takeUntil(this.isDestroying$)).subscribe((res) => {
      if (res) {
        this.hasRoutingError = res;
      }
    });

    this.instanceService.concurrencyDetected$?.pipe(takeUntil(this.isDestroying$)).subscribe(() => {
      this.hasConcurrencyIssue = true;
      this.concurrencyModal = true;
      this.cd.detectChanges();
    });
  }

  navigateToMenu(detail) {
    this.navigateToSection(detail);
  }

  getSnapShot() {
    return getDeepestRouteSnapshot(this.route)?.data;
  }

  getDeepestRouteInst() {
    return getDeepestRouteSnapshot(this.route)?.data?.instanceData?.instance;
  }

  getIsDraft() {
    return this.isDraft;
  }

  getViewType() {
    return this.viewType;
  }

  onValidate() {
    if (!this.getIsDraft()) {
      console.log('Skipped validating. Instance is not a draft');
      return;
    }
    this.CC.setCompletenessCheckData({ open: true, loading: true });

    if (this.hasReadOnlyMode$$()) {
      console.log('Read only mode validation, skip saving.');
      this.CC.validateInstance(this.instanceService.currentInstance)
        .pipe(
          map((res: any) => {
            if (res.success || res?.data?.status === 400) {
              this.CC.completenessCheckOthersMessage(res);
            } else {
              this.CC.setCompletenessCheckData({ open: true, fail: true });
            }
          }),
        )
        .toPromise();
      return;
    }

    this.onSave({
      section: this.sectionNavData?.id,
      view: this.getViewType(),
      state: { saveInstance: true, notify: false, beastNavEventKey: 'cc' },
    }).catch((e) => {
      console.warn('Problem when saving before completeness check');
    });
  }

  onContinue() {
    sessionStorage.removeItem('concurrencyDiff');
    const currentInstance = this.instanceService.currentInstance;
    const filingStatus = currentInstance?.instanceMetadata?.attributes?.filingStatus?.current;

    Promise.all(this.getHandleUnsavedArrays())
      .then((data) => {
        if (data.every((a: any) => a?.continueSaving)) {
          this.instanceService.saveInProgress = true;
        }
        currentInstance.instanceMetadata.attributes['hasConcurrencyIssue'] = false;
        currentInstance.instanceMetadata.attributes['hasUnprocessedConcurrentFiling'] = false;
        currentInstance.instanceMetadata.attributes['baselineVersion'] = currentInstance.instanceVersion + 1;
        this.instanceService.clearingConcurrencyFlag = true;
        return firstValueFrom(this.instanceService.saveInstance(currentInstance.id, currentInstance));
      })
      .then(() => {
        this.instanceService.clearingConcurrencyFlag = false;
        this.instanceService.saveInProgress = false;
        this.concurrencyModal = false;

        const concurrencySections: string[] = currentInstance?.instanceMetadata?.attributes?.concurrencySections || [];
        if (filingStatus === FILING_STATUS.ATTESTED && concurrencySections.includes('FORM')) {
          globalThis.finformUnsavedChanges = false;
          return firstValueFrom(this.instanceService.recallFiling());
        }
      })
      .then(() => {
        globalThis.finformUnsavedChanges = false;
        this.window.location.reload();
      })
      .catch((error) => {
        this.instanceService.clearingConcurrencyFlag = false;
        this.instanceService.saveInProgress = false;
        console.error('Error during continue:', error);
      });
  }

  onMigrationContinue() {
    this.migrationInProgress = true;
    this.migrationModal = false;
    this.instanceService.currentInstance.instanceMetadata.attributes['displayMigrationBanner'] = false;
    this.instanceService.saveInstance(this.instanceService.currentInstance.id, this.instanceService.currentInstance);
    this.onSave({ state: { beastNavEventKey: 'save' } }).finally(() => {
      this.migrationInProgress = false;
    });
  }

  navigateTo() {
    sessionStorage.setItem('concurrencyDiff', 'true');
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`${environment.dxtBasePath}/forms/mu3/view/formView/section/all`], {
        queryParams: { redlineCheck: true },
        queryParamsHandling: 'merge',
      }),
    );
    window.open(url, '_blank');
  }

  tapNavEvent(navEvent) {
    this.VPS.scrollToPosition([0, 0]);
    const { extras } = this.router.getCurrentNavigation();
    const { saveInstance, reviewed, initialView, beastNavEventKey, hasRoutingError } = extras?.state || {};

    if (navEvent['url'] === '/') {
      this.router.navigate(['/error']);
    }

    if (navEvent instanceof NavigationStart) {
      if (beastNavEventKey) {
        const eventConfig = this.beastEvent[beastNavEventKey];
        if (eventConfig) {
          const be: BeastEvent = {
            action: eventConfig?.action,
            resource: eventConfig?.resource,
            correlationId: this.instanceService?.currentInstance?.id,
            details: { description: eventConfig?.description, url: navEvent.url },
          };
          this.beast.postBeastEvent(be);
        } else {
          console.warn('No beastNavEventKey for:' + beastNavEventKey);
        }
      }
    }

    if (navEvent instanceof RoutesRecognized) {
      if (this.hasReadOnlyMode$$() && extras?.state?.autoSaveInstance) {
        extras.state.autoSaveInstance = false;
      }
      if (saveInstance) {
        this.DCS.resetToDefault();
        this.loadingText = `Saving Filing Data...`;
      }
      if (navEvent.url === '/create') {
        this.loadingText = `Creating MU3 Form`;
      }
      this.loadingService.setLoading(true);
    }
    this.viewFilingTypeBeastEvent(navEvent, initialView, reviewed);
    this.hasRoutingError = hasRoutingError;
  }

  onSave(options: { section?: string; view?: string; state: any } = { state: {} }) {
    if (!this.isDraft) {
      console.log('Skipped saving. Instance is not a draft');
      return Promise.reject('skipping save');
    }
    return Promise.all(this.getHandleUnsavedArrays()).then((data) => {
      if (!data.every((a: any) => a?.continueSaving)) {
        console.log('Not saving', data);
        return Promise.reject('Not Saving');
      } else {
        console.log('Saving...');
        this.instanceService.saveInProgress = true;
        let { licenseTypeCode, sectionId, viewType } = getDeepestRouteSnapshot(this.route).params;
        if (options.section) {
          sectionId = options.section;
          licenseTypeCode = undefined;
        }
        const navParams: any = ['view', options.view || viewType, 'section', sectionId];
        licenseTypeCode && navParams.push({ licenseTypeCode });
        const defaultState = { saveInstance: true, notify: true };
        return this.router.navigate(navParams, {
          queryParamsHandling: 'merge',
          state: { ...defaultState, ...options?.state },
        });
      }
    });
  }

  getHandleUnsavedArrays() {
    return handleUnsavedArrays();
  }

  getAppData() {
    const snapshot = getDeepestRouteSnapshot(this.route);
    const filingTypeCode = snapshot.data?.instanceData?.instance?.instanceMetadata?.attributes?.filingTypeCode || 'MU3';
    const params = snapshot.paramMap;
    const formSchemas = snapshot.data?.schemas;
    const sectionId = params?.get('sectionId');
    const licenseTypeCode = params?.get('licenseTypeCode');
    const viewType = params?.get('viewType') as ViewType;
    this.viewType = viewType;
    this.formView = this.viewType === 'formView';
    const navId = sectionId + (licenseTypeCode ? `;${licenseTypeCode}` : '');
    this.currentNavId = navId;

    return { viewType, filingTypeCode, navId, formSchemas };
  }

  mapNavEvent(cc) {
    this.isGuidance = this.router.url.includes('guidance');
    this.isPostSubmission = this.router.url.includes('post-submission');
    const { viewType, navId, formSchemas, filingTypeCode } = this.getAppData();

    if (!viewType || !filingTypeCode) {
      return [];
    }

    let instanceStatus;
    this.displayRulesService.displayRules$.subscribe((resp) => {
      instanceStatus = resp?.displayedStatus;
    });

    const filingStatus = this.instanceService?.currentInstance?.instanceMetadata?.attributes?.filingStatus?.current;
    if (
      (filingStatus === FILING_STATUS.SUBMITTED || filingStatus === FILING_STATUS.PROCESSED) &&
      viewType !== 'formView'
    ) {
      const urlMatch = this.router.url.match(/section\/([^?]+)/);
      let sectionId = urlMatch ? urlMatch[1] : 'identifyingInformation';
      if (!sectionId.startsWith('formView')) {
        sectionId = 'formView' + sectionId.charAt(0).toUpperCase() + sectionId.slice(1);
      }
      this.router.navigate(['view', 'formView', 'section', sectionId], {
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      return [];
    }

    if (
      (cc['open'] ||
        (cc['open'] === false && navId === 'attestAndSubmit') ||
        (cc['close'] && navId === 'attestAndSubmit') ||
        (cc['open'] === undefined && navId === 'attestAndSubmit')) &&
      instanceStatus !== 'Filing Processed' &&
      instanceStatus !== 'Failed Processing'
    ) {
      this.CC.onValidateCCOpened();
    }

    const snapshotData = this.getSnapShot();
    this.subjectId = snapshotData?.instanceData?.initialInstance?.instanceMetadata?.target?.subjectId;
    const legalName =
      snapshotData?.instanceData?.initialInstance?.collectedData?.individualInformation?.personalInformation
        ?.individualName;
    this.hasConcurrencyIssue = this.instanceService?.currentInstance?.instanceMetadata?.attributes?.hasConcurrencyIssue;
    this.displayMigrationBanner =
      this.instanceService?.currentInstance?.instanceMetadata?.attributes?.displayMigrationBanner;
    this.migrationInstanceId = this.instanceService?.currentInstance?.instanceMetadata?.attributes?.migrationInstanceId;

    let sectionText = this.navDataService.getNavData(`${viewType}.${filingTypeCode}`);
    if (sectionText?.[0]) {
      sectionText[0].text = `${legalName?.firstName ? legalName?.firstName + ' ' : ''}${
        legalName?.middleName ? legalName?.middleName + ' ' : ''
      }${legalName?.lastName ? legalName?.lastName + ' ' : ''}${legalName?.suffix ? legalName?.suffix : ''}`;
    }

    this.sectionNavData = this.navDataService.getNavItem(`${viewType}.${filingTypeCode}`, navId);
    this.title =
      (typeof formSchemas?.title === 'function' ? formSchemas?.title(this.sectionNavData) : formSchemas?.title) ??
      this.sectionNavData?.title ??
      '';

    if (
      !this.router.url.includes('/all') &&
      this.hasConcurrencyIssue &&
      this.viewType !== 'formView' &&
      !this.migrationInProgress &&
      !this.concurrencyModal
    ) {
      this.concurrencyModal = true;
    }
    if (this.displayMigrationBanner) {
      this.migrationModal = true;
    }

    this.instanceService.saveInProgress = false;
    if (snapshotData?.navData?.length) {
      snapshotData.navData = snapshotData.navData.map((data) => {
        if (data.type === 'header') {
          this.loggedInUserName = data.text;
          data['loggedInUserName'] = this.loggedInUserName;
          data.text = 'MU3 Form';
        }
        return data;
      });
    }
    this.navrouteData = snapshotData?.navData;
    return [...(snapshotData?.navData || [])];
  }

  navigateToSection(e, state = {}) {
    const hasUnsavedChanges = this.instanceService.hasUnsavedChanges();
    const isEditable = this.instanceService.isInstanceEditable(
      getDeepestRouteSnapshot(this.route).data.instanceData?.instance,
    );
    if (!e.navItem?.skip) {
      if (e.navItem?.id !== getDeepestRouteSnapshot(this.route).paramMap?.get('sectionId')) {
        let [id, licenseTypeCode] = (e.navItem?.id || '').split(';');
        const hasDirtyArrays = this.rampDoArrayDirtyCheck();
        const navParams = ['view', this.viewType, 'section', id];
        licenseTypeCode && navParams.push({ licenseTypeCode });
        const navExtras: NavigationExtras = {
          queryParams: { bannerType: null },
          queryParamsHandling: 'merge',
          state: { beastNavEventKey: `nav:${id}`, ...state },
        };
        if (hasDirtyArrays) {
          this.OS.openStayLeaveModal()
            .afterClosed$.toPromise()
            .then((response) => {
              if (response.data) {
                this.router.navigate(navParams, {
                  ...navExtras,
                  state: { ...navExtras.state, autoSaveInstance: isEditable && hasUnsavedChanges },
                });
              } else {
                this.router.navigateByUrl(this.router.routerState?.snapshot.url, navExtras);
              }
            });
        } else {
          console.log(`navigate: ${navParams}`, e);
          if (!this.inProgressNav) {
            this.inProgressNav = true;
            this.router
              .navigate(navParams, {
                ...navExtras,
                state: { ...navExtras.state, autoSaveInstance: isEditable && hasUnsavedChanges },
              })
              .then(() => {
                this.inProgressNav = false;
              });
          }
        }
      }
    }
  }

  rampDoArrayDirtyCheck() {
    return doArrayDirtyCheck();
  }

  onToggleRedline(value) {
    if (value) {
      const eventConfig = this.beastEvent['fv:redlining'];
      const event: BeastEvent = {
        action: eventConfig?.action,
        resource: eventConfig?.resource,
        details: { description: eventConfig.description },
        correlationId: this.instanceService.currentInstance.id,
      };
      this.beast.postBeastEvent(event);
      this.closeWidget('redline');

      const { instanceData, navData } = getDeepestRouteSnapshot(this.route).data;
      this.RL.setRedlineData(this.RL.classifyDiffData(instanceData, navData));
    } else {
      this.RL.setRedlineData({ open: false });
    }
  }

  onPrint() {
    this.onBeforePrint();
    setTimeout(() => {
      globalThis.print();
    }, 50);
  }

  onBeforePrint() {
    this.isPrintMode = true;
    document.querySelectorAll('rampdi-legal-view-encrypted-value').forEach((element) => {
      element.dispatchEvent(new CustomEvent('encrypt'));
    });
  }

  onAfterPrint() {
    this.isPrintMode = false;
  }

  getScreenSize(event?) {
    this.isMobileView = window.innerWidth < 1140;
  }

  public shouldWarnBeforeExit(): boolean {
    console.log('warn unsaved changes', globalThis?.finformUnsavedChanges);
    this.closeSnackbar();
    return globalThis?.finformUnsavedChanges ? true : false;
  }

  closeSnackbar() {
    if (this.OS.activeToastInstance) {
      this.OS.activeToastInstance.close();
    }
  }

  navigateToField($event) {
    const { item, section } = $event;
    const isOthersSectionWithNoNav = section.id === 'others';
    const isCurrentSection = section.id === this.currentNavId;

    if (isCurrentSection || isOthersSectionWithNoNav) {
      this.CC.gotoField(this.CC.getSelectors(item, section));
    } else {
      this.navigateToSection({ navItem: { id: section.id } });
      setTimeout(() => {
        this.CC.gotoField(this.CC.getSelectors(item, section));
      }, 2000);
    }
  }

  closeWidget(widget?: string) {
    if (widget === 'redline') {
      this.RL.setRedlineData({ open: false });
    } else {
      this.CC.setCompletenessCheckData({ open: false });
    }
  }

  ngAfterViewInit(): void {
    this.CC.setDisclosureDeleteModal?.(this.disclosureDeleteModal);
    this.CC.setAppComponent?.(this);
  }

  setupPhoneAutocompleteHandler() {
    this.phoneAutocompleteObserver = new MutationObserver(() => {
      const finraInputs = document.querySelectorAll('finra-input-text[autocomplete="phone"]');
      finraInputs.forEach((finraInput: any) => {
        if (finraInput.shadowRoot && !finraInput.dataset.phoneHandlerAdded) {
          finraInput.dataset.phoneHandlerAdded = 'true';
          const nativeInput = finraInput.shadowRoot.querySelector('input');
          if (nativeInput) {
            nativeInput.addEventListener('input', () => {
              const value = nativeInput.value;
              if (value && value.startsWith('+1')) {
                nativeInput.value = value.replace(/^\+1\s?/, '');
                nativeInput.dispatchEvent(new Event('input', { bubbles: true }));
              }
            });
          }
        }
      });
    });
    this.phoneAutocompleteObserver.observe(document.body, { childList: true, subtree: true });
  }

  ngOnDestroy() {
    this.isDestroying$.next(undefined);
    this.isDestroying$.complete();
    if (this.phoneAutocompleteObserver) {
      this.phoneAutocompleteObserver.disconnect();
    }
  }
}
